import { useState } from 'react';
import { CLASSE_SURVOL, useListeClavier } from '../hooks/useListeClavier';
import { IconeCrayon, Infobulle } from './Elements';
import { formaterPlage, libelleJour } from '../lib/format';
import type { Creneau } from '../types';

/**
 * Choix d'un creneau, a la saisie — meme geste que le choix d'une
 * activite ou d'un membre du personnel : on tape, la liste se filtre.
 *
 * La recherche porte sur tout ce qui identifie un creneau a l'oeil : le
 * jour, l'heure, l'encadrant, le lieu. « merc 17 » ou « orso » suffisent.
 *
 * Le creneau est FACULTATIF (RG-24 signale l'inscription qui n'en a pas)
 * : « Sans créneau » est donc une option a part entiere, pas un vide.
 */
export function ChoixCreneau({
  creneaux,
  valeur,
  onChanger,
  mention,
  placeholder = 'Taper un jour, une heure, un professeur…',
  avecActivite = false,
  sansOptionVide = false,
}: {
  creneaux: Creneau[];
  valeur: string;
  onChanger: (id: string) => void;
  /** Suffixe par creneau : « complet », « en tolérance »… */
  mention?: (creneau: Creneau) => string;
  placeholder?: string;
  /**
   * Nommer l'activite devant le jour. Inutile a la saisie — l'activite
   * est deja choisie au-dessus et tous les creneaux sont les siens —,
   * indispensable au TRANSFERT, ou la liste les traverse toutes.
   */
  avecActivite?: boolean;
  /**
   * Retirer l'option « Sans créneau ». Un transfert vise un creneau par
   * definition : le laisser proposerait de vider l'horaire au lieu de le
   * changer.
   */
  sansOptionVide?: boolean;
}) {
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(false);

  const choisi = creneaux.find((c) => c.id === valeur) ?? null;

  const decrire = (c: Creneau) =>
    [
      avecActivite ? c.activite_libelle : null,
      `${libelleJour(c.jour)} ${formaterPlage(c.heure_debut, c.heure_fin)}`,
      c.encadrants[0]?.prenom_usuel,
      c.lieu_code,
    ]
      .filter(Boolean)
      .join(' · ');

  const effectif = (c: Creneau) =>
    `${c.effectif} inscrit(s)${c.capacite_max ? ` / ${c.capacite_max}` : ''}`;

  const candidats = creneaux
    .filter((c) => {
      const terme = recherche.trim().toLowerCase();
      if (!terme) return true;
      return decrire(c).toLowerCase().includes(terme);
    })
    .slice(0, 12);

  const choisir = (creneau: Creneau | null) => {
    onChanger(creneau?.id ?? '');
    setRecherche('');
    setOuvert(false);
  };
  // « Sans creneau » est la PREMIERE option de la liste : le clavier doit
  // pouvoir l'atteindre comme les autres, d'ou le `null` en tete.
  const clavier = useListeClavier<Creneau | null>(
    sansOptionVide ? candidats : [null, ...candidats],
    choisir,
  );

  /*
   * AU REPOS, LE CHAMP EST GRISE.
   *
   * Un creneau deja choisi n'a pas a ressembler a une zone de saisie :
   * le modifier est un geste delibere, pas un effleurement. Le champ
   * reprend donc l'aspect que `.champ` donne a un `disabled` — fond
   * `encre-100`, curseur inerte — et le crayon, seul element actif,
   * ouvre la recherche.
   *
   * Le crayon plutot que le mot « Changer » : c'est le meme geste que
   * sur la grille du calendrier, et il se reconnait d'un ecran a
   * l'autre.
   */
  if (choisi && !ouvert) {
    return (
      <div className="flex items-center gap-2">
        <span className="champ flex flex-1 cursor-default items-center justify-between gap-2 bg-encre-100 text-encre-600">
          <span className="truncate">
            {decrire(choisi)}{' '}
            <span className="text-encre-500">
              — {effectif(choisi)}
              {mention?.(choisi) ? ` ${mention(choisi)}` : ''}
            </span>
          </span>
        </span>
        <Infobulle texte="Changer de créneau" className="inline-flex shrink-0">
          <button
            type="button"
            aria-label="Changer de créneau"
            className="rounded p-1.5 text-encre-400 hover:bg-encre-100 hover:text-encre-800"
            onClick={() => {
              setRecherche('');
              setOuvert(true);
            }}
          >
            <IconeCrayon classe="h-4 w-4" />
          </button>
        </Infobulle>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className="champ"
        value={recherche}
        placeholder={placeholder}
        onChange={(evenement) => {
          setRecherche(evenement.target.value);
          setOuvert(true);
        }}
        onFocus={() => setOuvert(true)}
        onKeyDown={clavier.auClavier}
        onBlur={() => window.setTimeout(() => setOuvert(false), 150)}
      />

      {ouvert && (
        <ul
          ref={clavier.liste}
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-encre-200 bg-white shadow-lg"
        >
          {/* « Sans creneau » ouvre la liste : c'est un choix valable, pas
              un defaut d'absence. Un TRANSFERT, lui, vise un creneau par
              definition — l'option disparait, et les index du clavier se
              decalent d'autant. */}
          {!sansOptionVide && (
            <li data-index={0}>
              <button
                type="button"
                className={`block w-full border-b border-encre-100 px-3 py-2 text-left text-sm text-encre-500 ${
                  clavier.index === 0 ? CLASSE_SURVOL : ''
                }`}
                onMouseEnter={() => clavier.survoler(0)}
                onClick={() => choisir(null)}
              >
                — Sans créneau —
              </button>
            </li>
          )}
          {candidats.length === 0 ? (
            <li className="px-3 py-2 text-sm text-encre-400">Aucun créneau ne correspond.</li>
          ) : (
            candidats.map((creneau, rang) => (
              <li key={creneau.id} data-index={sansOptionVide ? rang : rang + 1}>
                <button
                  type="button"
                  className={`flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left text-sm ${
                    clavier.index === (sansOptionVide ? rang : rang + 1) ? CLASSE_SURVOL : ''
                  }`}
                  onMouseEnter={() => clavier.survoler(sansOptionVide ? rang : rang + 1)}
                  onClick={() => choisir(creneau)}
                >
                  <span className="truncate">{decrire(creneau)}</span>
                  <span className="shrink-0 text-xs text-encre-500">
                    {effectif(creneau)}
                    {mention?.(creneau) ? ` ${mention(creneau)}` : ''}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
